import { LogOut, UserCircle2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { GFS_LOGO_URL } from "@/lib/brand";
import ThemeToggle from "@/components/ThemeToggle";

export default function Topbar({ title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canHaveProfile = !!user;
  const onProfile = location.pathname === "/profile";
  const isCollector = user?.role === "collector";
  // Map each role back to its home dashboard so "Back to dashboard" links
  // the admin/viewer/superadmin to the right place too.
  const homeForRole = {
    collector: "/collect",
    facility: "/facility",
    admin: "/admin",
    viewer: "/viewer",
    superadmin: "/superadmin",
  };
  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#D2E4D6]"
      data-testid="topbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={GFS_LOGO_URL}
            alt="GreenForest Solutions"
            data-testid="brand-logo"
            className="w-10 h-10 object-contain"
          />
          <div className="border-l border-[#D2E4D6] pl-3">
            <div className="font-heading font-semibold text-[#000000] leading-none">
              PRO<span className="text-[#056839]">cess</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#69A488] mt-0.5">
              {subtitle || "Traceability Core"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {title && (
            <div className="hidden md:block text-sm text-[#3F3F3F]" data-testid="topbar-title">
              {title}
            </div>
          )}
          <ThemeToggle />
          {user && (
            <>
              <div className="hidden sm:flex flex-col items-end" data-testid="topbar-user">
                <div className="text-sm text-[#3F3F3F] font-medium" data-testid="topbar-user-name">{user.name}</div>
                {(user.facility_name || user.company_name) && (
                  <div
                    className="text-[11px] text-[#3F3F3F]/70 leading-tight max-w-[200px] truncate"
                    title={user.facility_name || user.company_name}
                    data-testid="topbar-org-name"
                  >
                    {user.facility_name || user.company_name}
                  </div>
                )}
                {user.role === "superadmin" ? (
                  <div
                    className="text-[10px] uppercase tracking-[0.18em] text-white bg-[#0F2E2A] px-2 py-0.5 rounded-md font-semibold mt-0.5"
                    data-testid="topbar-role-label"
                  >
                    GFS Admin
                  </div>
                ) : (
                  <div className="text-[10px] uppercase tracking-wider text-[#69A488] mt-0.5" data-testid="topbar-role-label">
                    {user.role}
                  </div>
                )}
              </div>
              {isCollector && (
                <Link
                  to="/collect?agents=1"
                  data-testid="my-agents-link"
                  className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 h-9 rounded-md transition-colors text-[#056839] hover:bg-[#D2E4D6]/60"
                >
                  <Users className="w-4 h-4" />
                  My collection agents
                </Link>
              )}
              {canHaveProfile && (
                <Link
                  to={onProfile ? (homeForRole[user.role] || "/") : "/profile"}
                  data-testid="profile-link"
                  className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 h-9 rounded-md transition-colors ${
                    onProfile
                      ? "text-[#3F3F3F] hover:bg-[#D2E4D6]/40"
                      : "text-[#056839] hover:bg-[#D2E4D6]/60"
                  }`}
                >
                  <UserCircle2 className="w-4 h-4" />
                  {onProfile ? "Back to dashboard" : "My profile"}
                </Link>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="logout-btn"
                className="text-[#A0522D] hover:bg-[#E4D5B7]/40"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Logout
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
